import { InvestigationPriority, InvestigationStatus } from '../../investigation/types';

export enum FindingType {
  ASN_REPORT = 0,
  CERTIFICATE_REPORT = 1,
  DOMAIN = 2,
  SIMILAR_DOMAIN = 3,
  DNS_RECORD = 4,
  IP_BLOCK = 5,
  IP_4 = 6,
  IP_6 = 7,
  EMAIL = 8,
  SERVICE = 9,
}

export enum FindingClassType {
  ASSET = 0,
  ARTIFACT = 1,
}

export interface RelatedFinding {
  name: string;
  uniqueIdentifier: string;
  type: FindingType;
}

export const findingTypeToIconMap: { [key in FindingType]: string } = {
  [FindingType.ASN_REPORT]: 'icon-asn-report',
  [FindingType.CERTIFICATE_REPORT]: 'icon-certificate-report',
  [FindingType.DOMAIN]: 'icon-domain',
  [FindingType.SIMILAR_DOMAIN]: 'icon-similar-domain',
  [FindingType.DNS_RECORD]: 'icon-dns-record',
  [FindingType.IP_BLOCK]: 'icon-ip-block',
  [FindingType.IP_4]: 'icon-ip4',
  [FindingType.IP_6]: 'icon-ip6',
  [FindingType.EMAIL]: 'icon-email',
  [FindingType.SERVICE]: 'icon-service',
};

export interface EasmFinding {
  _id: string;
  class: number;
  name: string;
  type: FindingType;
  nextScanStatus: string;
  email?: string;
  domain?: string;
  ip?: string;
  country?: string;
  operator?: string;
  expirationStatus?: number;
  uniqueIdentifier?: string;
  status?: string;
  relatedSummary?: RelatedSummary;
  assetsRelatedToArtifact?: DiscoveredAssets;
  assetsRelatedToAsset?: DiscoveredAssets;
  artifactsRelatedToAsset?: DiscoveredArtifacts;
  openPorts?: number;
  cvesFromRelatedArtifacts?: number;
  relatedArtifactAreVulnerable?: boolean;
  dnsResolutionFailed?: boolean;
  expirationStatusFromArtifacts?: number;
  metadata: {
    investigation: {
      status: InvestigationStatus;
      priority: InvestigationPriority;
      notesCount: number;
    };
  };
  assignee: Assignee | null;
}

export interface RelatedSummary {
  artifacts?: DiscoveredArtifacts;
  assets?: DiscoveredAssets;
}

export interface DiscoveredArtifacts {
  asn?: number;
  certificate?: number;
  soonExpireCertificates?: number;
  expiredCertificates?: number;
  dnsRecords?: number;
  vulnerableDnsServer?: number;
  dnsResolutionFailed?: number;
  services?: number;
  vulnerabilities?: number;
  openPorts?: number;
  cves?: number;
  ipBlocks?: number;
}

export interface DiscoveredAssets {
  domains?: number;
  similarDomains?: number;
  ipv4?: number;
  ipv6?: number;
  emails?: number;
}

export interface Assignee {
  _id?: string;
  company?: {
    id: string;
    name: string;
  };
  profile: {
    fullName: string;
  };
}

export const RELATED_ASSETS_COLUMN_ORDER = ['domains', 'similarDomains', 'ipv4', 'ipv6', 'emails'];

export const RELATED_ARTIFACTS_COLUMN_ORDER = ['asn', 'certificate', 'ipBlocks', 'dnsRecords', 'services'];

export const COLUMN_TO_FINDING_TYPE_MAP = {
  domains: FindingType.DOMAIN,
  similarDomains: FindingType.SIMILAR_DOMAIN,
  ipv4: FindingType.IP_4,
  ipv6: FindingType.IP_6,
  emails: FindingType.EMAIL,
  asn: FindingType.ASN_REPORT,
  certificate: FindingType.CERTIFICATE_REPORT,
  ipBlocks: FindingType.IP_BLOCK,
  dnsRecords: FindingType.DNS_RECORD,
  services: FindingType.SERVICE,
};

export enum ExpirationStatus {
  NOT_SOON_TO_EXPIRE = 0,
  SOON_TO_EXPIRE = 1,
  EXPIRED = 2,
}

export const FINDING_TYPE_TO_ICON_MAP: Map<number, string> = new Map<number, string>([
  [FindingType.ASN_REPORT, 'icon-asn-report'],
  [FindingType.CERTIFICATE_REPORT, 'icon-certificate-report'],
  [FindingType.DOMAIN, 'icon-domain'],
  [FindingType.SIMILAR_DOMAIN, 'icon-similar-domain'],
  [FindingType.DNS_RECORD, 'icon-dns-record'],
  [FindingType.IP_BLOCK, 'icon-ip-block'],
  [FindingType.IP_4, 'icon-ip4'],
  [FindingType.IP_6, 'icon-ip6'],
  [FindingType.EMAIL, 'icon-email'],
  [FindingType.SERVICE, 'icon-service'],
]);

export enum EASM_SMARTVIEWS_LABELS {
  ALL_ARTIFACTS = 'all_artifacts',
  ALL_ASSETS = 'all_assets',
}

export const UNAVAILABLE_DATA = '-';

export const COLUMN_RELATED_FINDINGS_CATEGORY_LIMIT = 99;

export enum ErrorCodes {
  FEATURE_NOT_AVAILABLE_FOR_COMPANY = 2321,
}

export const MAX_NUMBER_OF_FINDINGS_TO_BE_SELECTED = 500;

export interface PotentialAssignee {
  _id: string;
  name: string;
}

export enum Events {
  CHANGE_PROPERTY = 'changeProperty',
}

